import PnpItem from './PnpItem';
import Item from './Item';
import TestPart from './TestPart';
import RubricBlock from './RubricBlock';
import DeliveryOptionsProvider from './DeliveryOptionsProvider';
import { ISection, ItemInterface } from '@/controllers/session/models/IQtiSession';
import Utility from '@/util/Utility';
import ItemStatus from '@/status/ItemStatus';

class Section extends DeliveryOptionsProvider {
  public static readonly ITEM_RELEASED_BY_PROCTOR_BIT_POSITION = 0;
  private readonly _identifier: string;
  private readonly _title: string;
  private readonly _visible: boolean;
  private readonly _required: boolean;
  private _releasedByProctor = false;

  private readonly _rubricBlocks: Array<RubricBlock> = new Array<RubricBlock>();
  private readonly _pnpList: Array<PnpItem> = new Array<PnpItem>();
  private readonly _subSections: Array<Section> = new Array<Section>();
  private readonly _items: Array<Item> = new Array<Item>();
  private _section: Section | null = null;

  private sectionTestPart!: TestPart;

  constructor(identifier: string, title: string, visible: boolean, required: boolean) {
    super();
    this._identifier = identifier;
    this._title = title;
    this._visible = visible;
    this._required = required;
  }

  get testPart(): TestPart {
    return this.findTopLevelSection(this).sectionTestPart;
  }

  set testPart(value: TestPart) {
    this.sectionTestPart = value;
  }

  get section(): Section | null {
    return this._section;
  }

  set section(value: Section | null) {
    this._section = value;
  }

  get identifier(): string {
    return this._identifier;
  }

  get title(): string {
    return this._title;
  }

  get visible(): boolean {
    return this._visible;
  }

  get required(): boolean {
    return this._required;
  }

  get rubricBlocks(): Array<RubricBlock> {
    return this._rubricBlocks;
  }

  get pnpList(): Array<PnpItem> {
    return this._pnpList;
  }

  get items(): Array<Item> {
    return this._items;
  }

  get subSections(): Array<Section> {
    return this._subSections;
  }

  set releasedByProctor(value: boolean) {
    this._releasedByProctor = value;
  }

  get releasedByProctor(): boolean {
    return this._releasedByProctor;
  }

  getFlaggedCount(): number {
    return this._items.filter((item) => item.isFlagged).length;
  }

  getIncompleteCount(): number {
    return this._items.filter((item) => item.itemStatus !== ItemStatus.FULLY_ATTEMPTED).length;
  }

  addRubricBlock(rubricBlock: RubricBlock) {
    this._rubricBlocks.push(rubricBlock);
    rubricBlock.section = this;
  }

  addPnpItem(pnpItem: PnpItem) {
    this._pnpList.push(pnpItem);
    pnpItem.section = this;
  }

  addItem(item: Item) {
    this._items.push(item);
    item.section = this;
  }

  addSubSection(section: Section) {
    this._subSections.push(section);
    section.section = this;
  }

  findTopLevelSection(section): Section {
    if (section.sectionTestPart) {
      return section;
    } else {
      return this.findTopLevelSection(section._section);
    }
  }

  toSectionInterface(): ISection {
    let state = 0;
    if (this._releasedByProctor) {
      state = Utility.setBit(state, Section.ITEM_RELEASED_BY_PROCTOR_BIT_POSITION);
    }
    const convertedItems: Array<ItemInterface> = this.items
      .map((item) => item.toItemInterface())
      .filter((item) => item.state != 0);
    return {
      identifier: this.identifier,
      items: convertedItems.length > 0 ? convertedItems : undefined,
      state: state,
      deliveryOptions: this.getDeliveryOptionsToSave(),
    };
  }
}

export default Section;
